/**
 * CongesTab — "Mon Espace → Congés" (bureau view).
 *
 * Three panels:
 *   1. Balance cards   (jours légaux restants + compteur récup en heures)
 *   2. New request form (period + half-day flags + type + reason)
 *   3. Request history (pending / approved / denied)
 *
 * Admin team view + approval flow live in the Équipe tab (Phase E).
 */

import React, { useMemo, useState } from 'react';
import {
  CalendarDaysIcon,
  ClockIcon,
  PlusIcon,
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import {
  useMyLeaveBalance,
  useMyLeaveRequests,
  useCreateLeaveRequest,
  useCancelLeaveRequest,
  useMyUnvalidatedWeeks,
  useHolidays,
  useMyLeaveConfig,
} from '../hooks/useLeave';
import { CreateLeaveRequestPayload, LeaveRequest, LeaveStatus, LeaveType, HalfDayPart, UnvalidatedWeek, EmploymentType } from '../api/leave.api';
import { LeaveCalendar, CalendarEntry } from '../components/LeaveCalendar/LeaveCalendar';
import { formatHoursAsHM, formatSignedHoursAsHM } from '../utils/time';
import { expandLeaveRequest, workingDowSet } from '../components/LeaveCalendar/expandRequest';
import './CongesTab.css';

const TYPE_LABEL: Record<LeaveType, string> = {
  legal: 'Congé légal',
  recup: 'Récupération',
  unpaid: 'Sans solde',
  sick: 'Maladie',
};

const STATUS_LABEL: Record<LeaveStatus, string> = {
  pending: 'En attente',
  approved: 'Approuvé',
  denied: 'Refusé',
  cancelled: 'Annulé',
};

const HALF_LABEL: Record<HalfDayPart, string> = {
  full: 'Journée',
  am: 'Matin',
  pm: 'Après-midi',
};

const fmtDate = (iso: string) => {
  // iso = 'YYYY-MM-DD'
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

// --- Balance cards ---------------------------------------------------------

const BalanceCards: React.FC<{ year: number }> = ({ year }) => {
  const { data: bal, isLoading } = useMyLeaveBalance(year);

  if (isLoading || !bal) {
    return <div className="conge-loading">Chargement du solde…</div>;
  }

  const isEmployee = bal.employment_type === 'employee';

  return (
    <div className="conge-balance-row">
      {isEmployee && (
        <div className="conge-balance-card conge-balance-card--legal">
          <div className="conge-balance-header">
            <CalendarDaysIcon className="conge-balance-icon" />
            <span className="conge-balance-label">Congés légaux {bal.year}</span>
          </div>
          <div className="conge-balance-value">{bal.legal_remaining}</div>
          <div className="conge-balance-sub">
            jours restants <span className="conge-balance-sub-dim">sur {bal.legal_quota}</span>
          </div>
          <div className="conge-balance-used">{bal.legal_used} jour{bal.legal_used > 1 ? 's' : ''} pris</div>
        </div>
      )}

      {isEmployee ? (
        <div className={`conge-balance-card conge-balance-card--recup ${bal.recup_hours_balance < 0 ? 'is-deficit' : ''}`}>
          <div className="conge-balance-header">
            <ClockIcon className="conge-balance-icon" />
            <span className="conge-balance-label">Compteur récup</span>
          </div>
          <div className="conge-balance-value">
            {formatSignedHoursAsHM(bal.recup_hours_balance)}
          </div>
          <div className="conge-balance-sub">
            base {bal.base_hours_per_week}h/sem. · depuis {fmtDate(bal.recup_tracking_start)}
          </div>
          <div className="conge-balance-used">
            {bal.recup_used > 0 ? `${bal.recup_used} jour${bal.recup_used > 1 ? 's' : ''} récup déjà pris` : 'Aucune récup prise'}
          </div>
        </div>
      ) : (
        <div className="conge-balance-card conge-balance-card--hours">
          <div className="conge-balance-header">
            <ClockIcon className="conge-balance-icon" />
            <span className="conge-balance-label">Heures validées</span>
          </div>
          <div className="conge-balance-value">{formatHoursAsHM(bal.total_validated_hours)}</div>
          <div className="conge-balance-sub">
            depuis {fmtDate(bal.recup_tracking_start)}
          </div>
          <div className="conge-balance-used">
            Indépendant · pas de compteur récup
          </div>
        </div>
      )}

      {bal.pending_count > 0 && (
        <div className="conge-balance-card conge-balance-card--pending">
          <div className="conge-balance-header">
            <ExclamationCircleIcon className="conge-balance-icon" />
            <span className="conge-balance-label">En attente</span>
          </div>
          <div className="conge-balance-value">{bal.pending_count}</div>
          <div className="conge-balance-sub">
            demande{bal.pending_count > 1 ? 's' : ''} à approuver
          </div>
        </div>
      )}
    </div>
  );
};

// --- New request form ------------------------------------------------------

const NewRequestForm: React.FC<{ prefillDate?: string | null; onSubmitted: () => void; onClose: () => void }> = ({ prefillDate, onSubmitted, onClose }) => {
  const today = new Date().toISOString().slice(0, 10);
  const initial = prefillDate || today;
  const [dateFrom, setDateFrom] = useState(initial);
  const [dateTo, setDateTo] = useState(initial);
  const [halfDayStart, setHalfDayStart] = useState<HalfDayPart>('full');
  const [halfDayEnd, setHalfDayEnd] = useState<HalfDayPart>('full');
  const [type, setType] = useState<LeaveType>('legal');
  const [reason, setReason] = useState('');
  const createMut = useCreateLeaveRequest();
  // Show remaining quota inline so the user knows what they have left
  // when they pick a leave type.
  const requestYear = parseInt(dateFrom.slice(0, 4), 10) || new Date().getFullYear();
  const { data: balance } = useMyLeaveBalance(requestYear);

  const submit = async () => {
    if (!dateFrom || !dateTo) return;
    if (dateTo < dateFrom) {
      toast.error("La date de fin doit être après la date de début");
      return;
    }
    if (dateFrom.slice(0, 4) !== dateTo.slice(0, 4)) {
      toast.error("Une demande ne peut pas chevaucher plusieurs années. Crée deux demandes séparées.");
      return;
    }
    try {
      const payload: CreateLeaveRequestPayload = {
        dateFrom,
        dateTo,
        halfDayStart: dateFrom === dateTo ? halfDayStart : halfDayStart,
        halfDayEnd: dateFrom === dateTo ? halfDayStart : halfDayEnd,
        type,
        reason: reason.trim() || undefined,
      };
      await createMut.mutateAsync(payload);
      toast.success('Demande envoyée — en attente d\'approbation');
      onSubmitted();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur lors de l\'envoi');
    }
  };

  const sameDay = dateFrom === dateTo;

  return (
    <div className="conge-form">
      <div className="conge-form-header">
        <h3>Nouvelle demande de congé</h3>
        <button className="conge-icon-btn" onClick={onClose}>
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      <div className="conge-form-grid">
        <div className="conge-form-group">
          <label>Du</label>
          <input type="date" value={dateFrom} onChange={e => {
            setDateFrom(e.target.value);
            if (dateTo < e.target.value) setDateTo(e.target.value);
          }} />
        </div>
        <div className="conge-form-group">
          <label>Au</label>
          <input type="date" value={dateTo} min={dateFrom} onChange={e => setDateTo(e.target.value)} />
        </div>
      </div>

      <div className="conge-form-grid">
        <div className="conge-form-group">
          <label>{sameDay ? 'Période' : 'Premier jour'}</label>
          <select value={halfDayStart} onChange={e => setHalfDayStart(e.target.value as HalfDayPart)}>
            <option value="full">Journée complète</option>
            <option value="am">Matin uniquement</option>
            <option value="pm">Après-midi uniquement</option>
          </select>
        </div>
        {!sameDay && (
          <div className="conge-form-group">
            <label>Dernier jour</label>
            <select value={halfDayEnd} onChange={e => setHalfDayEnd(e.target.value as HalfDayPart)}>
              <option value="full">Journée complète</option>
              <option value="am">Matin uniquement</option>
              <option value="pm">Après-midi uniquement</option>
            </select>
          </div>
        )}
      </div>

      <div className="conge-form-group">
        <label>
          Type
          {balance?.employment_type === 'employee' && type === 'legal' && (
            <span className="conge-quota-hint">
              · {balance.legal_remaining} j légal restants ({balance.legal_used} / {balance.legal_quota})
            </span>
          )}
          {balance?.employment_type === 'employee' && type === 'recup' && (
            <span className={`conge-quota-hint ${balance.recup_hours_balance < 0 ? 'is-negative' : ''}`}>
              · solde récup {formatSignedHoursAsHM(balance.recup_hours_balance)}
            </span>
          )}
          {balance?.employment_type === 'independent' && (
            <span className="conge-quota-hint">
              · indépendant — pas de quota / solde
            </span>
          )}
        </label>
        <div className="conge-type-pills">
          {(['legal', 'recup', 'unpaid', 'sick'] as LeaveType[]).map(t => (
            <button
              key={t}
              type="button"
              className={`conge-type-pill ${type === t ? 'is-active' : ''}`}
              onClick={() => setType(t)}
            >
              {TYPE_LABEL[t]}
            </button>
          ))}
        </div>
      </div>

      <div className="conge-form-group">
        <label>Motif (optionnel)</label>
        <textarea
          rows={2}
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Ex: vacances famille, RDV médical…"
        />
      </div>

      <div className="conge-form-footer">
        <button className="conge-btn-ghost" onClick={onClose}>Annuler</button>
        <button
          className="conge-btn-primary"
          onClick={submit}
          disabled={createMut.isPending || !dateFrom || !dateTo}
        >
          {createMut.isPending ? 'Envoi…' : 'Envoyer la demande'}
        </button>
      </div>
    </div>
  );
};

// --- Request row -----------------------------------------------------------

const RequestRow: React.FC<{ req: LeaveRequest }> = ({ req }) => {
  const cancelMut = useCancelLeaveRequest();

  const dateRange = useMemo(() => {
    if (req.date_from === req.date_to) {
      const suffix = req.half_day_start === 'full' ? '' : ` (${HALF_LABEL[req.half_day_start].toLowerCase()})`;
      return `${fmtDate(req.date_from)}${suffix}`;
    }
    return `${fmtDate(req.date_from)} → ${fmtDate(req.date_to)}`;
  }, [req]);

  return (
    <div className={`conge-row conge-row--${req.status}`}>
      <div className="conge-row-main">
        <div className="conge-row-dates">{dateRange}</div>
        <div className="conge-row-meta">
          <span className="conge-row-type">{TYPE_LABEL[req.type]}</span>
          {req.reason && <span className="conge-row-reason">— {req.reason}</span>}
        </div>
        {req.decision_reason && (
          <div className="conge-row-decision">Note: {req.decision_reason}</div>
        )}
      </div>
      <div className="conge-row-status">
        {req.status === 'pending' && <span className="conge-status conge-status--pending"><ExclamationCircleIcon className="w-4 h-4" />{STATUS_LABEL.pending}</span>}
        {req.status === 'approved' && <span className="conge-status conge-status--approved"><CheckCircleIcon className="w-4 h-4" />{STATUS_LABEL.approved}</span>}
        {req.status === 'denied' && <span className="conge-status conge-status--denied"><XCircleIcon className="w-4 h-4" />{STATUS_LABEL.denied}</span>}
        {req.status === 'cancelled' && <span className="conge-status conge-status--cancelled">{STATUS_LABEL.cancelled}</span>}
      </div>
      {req.status === 'pending' && (
        <button
          className="conge-btn-ghost-sm"
          onClick={() => {
            if (confirm('Annuler cette demande ?')) {
              cancelMut.mutate(req.id, {
                onSuccess: () => toast.success('Demande annulée'),
                onError: () => toast.error('Erreur'),
              });
            }
          }}
        >
          Annuler
        </button>
      )}
    </div>
  );
};

// --- Unvalidated weeks panel ----------------------------------------------

const UnvalidatedWeeksPanel: React.FC<{ year: number; employmentType: EmploymentType; onValidateWeek?: (weekStart: string) => void }> = ({ year, employmentType, onValidateWeek }) => {
  const { data: weeks = [], isLoading } = useMyUnvalidatedWeeks(year);

  if (isLoading) return null;
  if (weeks.length === 0) return null;

  // Don't validate from here — open the week in the Planning tab so the user can
  // review/correct the hours and explicitly certify them before validating.
  const handleValidate = (w: UnvalidatedWeek) => {
    onValidateWeek?.(w.week_start);
  };

  return (
    <div className="conge-unvalidated">
      <div className="conge-unvalidated-header">
        <ExclamationCircleIcon className="w-5 h-5" />
        <strong>{weeks.length} semaine{weeks.length > 1 ? 's' : ''} à valider</strong>
        <span className="conge-unvalidated-hint">
          {employmentType === 'employee'
            ? 'Vérifiez vos heures puis validez la semaine. Tant qu\'une semaine n\'est pas validée, elle ne compte pas dans votre compteur récup.'
            : 'Vérifiez vos heures puis validez la semaine pour figer le décompte (utile pour la facturation).'}
        </span>
      </div>
      <div className="conge-unvalidated-list">
        {weeks.map(w => (
          <div key={w.week_start} className="conge-unvalidated-row">
            <span className="conge-unvalidated-week">Semaine du {fmtDate(w.week_start)}</span>
            <span className="conge-unvalidated-hours">{formatHoursAsHM(w.planned_hours)} prévues</span>
            <button
              className="conge-btn-primary conge-btn-sm"
              onClick={() => handleValidate(w)}
            >
              <CheckCircleIcon className="w-4 h-4" /> Vérifier &amp; valider
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// --- Main tab --------------------------------------------------------------

const ymd = (d: Date) => d.toISOString().slice(0, 10);

export const CongesTab: React.FC<{ onValidateWeek?: (weekStart: string) => void }> = ({ onValidateWeek }) => {
  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(now.getMonth());
  const [showForm, setShowForm] = useState(false);
  const [prefillDate, setPrefillDate] = useState<string | null>(null);
  const { data: requests, isLoading } = useMyLeaveRequests(year);
  const { data: myBalance } = useMyLeaveBalance(year);
  const { data: myConfig } = useMyLeaveConfig(year);
  const employmentType: EmploymentType = myBalance?.employment_type || 'independent';

  // Date range covering the calendar grid (always full 6-week window — safe upper bound)
  const monthRange = useMemo(() => {
    const start = new Date(Date.UTC(year, month, 1));
    start.setUTCDate(start.getUTCDate() - 14);
    const end = new Date(Date.UTC(year, month + 1, 0));
    end.setUTCDate(end.getUTCDate() + 14);
    return { from: ymd(start), to: ymd(end) };
  }, [year, month]);

  const { data: holidays = [] } = useHolidays(monthRange);

  // Build entriesByDate from my requests (approved + pending) and holidays.
  // The expansion skips holidays + non-working days like the backend does, so
  // a chip is only shown on days that actually consume quota. (Holidays are
  // loaded for the visible grid window, which covers every rendered cell.)
  const entriesByDate = useMemo(() => {
    const m = new Map<string, CalendarEntry[]>();
    const holidaySet = new Set(holidays.map(h => h.date));
    const expandOpts = { holidays: holidaySet, workingDays: workingDowSet(myConfig?.working_days) };
    for (const h of holidays) {
      const arr = m.get(h.date) || [];
      arr.push({
        kind: 'holiday',
        label: h.label,
        tooltip: `Férié: ${h.label}`,
      });
      m.set(h.date, arr);
    }
    for (const req of requests || []) {
      if (req.status === 'cancelled' || req.status === 'denied') continue;
      const days = expandLeaveRequest(req, expandOpts);
      for (const d of days) {
        const arr = m.get(d.date) || [];
        arr.push({
          kind: 'leave',
          label: TYPE_LABEL[req.type],
          type: req.type,
          status: req.status,
          fraction: d.fraction,
          tooltip: `${TYPE_LABEL[req.type]} (${req.status === 'pending' ? 'en attente' : 'approuvé'})${d.fraction < 1 ? ' · demi-journée' : ''}`,
        });
        m.set(d.date, arr);
      }
    }
    return m;
  }, [requests, holidays, myConfig?.working_days]);

  const handleDayClick = (dateIso: string) => {
    // Don't preopen the form on cells outside the current visible month —
    // clicking on a chip-only day is the one ambiguous case but starting a
    // new request there is still a valid intent.
    setPrefillDate(dateIso);
    setShowForm(true);
  };

  const goPrevMonth = () => {
    if (month === 0) { setMonth(11); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const goNextMonth = () => {
    if (month === 11) { setMonth(0); setYear(year + 1); }
    else setMonth(month + 1);
  };
  const goToday = () => { setYear(currentYear); setMonth(now.getMonth()); };

  const sorted = useMemo(() => {
    if (!requests) return [];
    return [...requests].sort((a, b) => {
      if (a.status === 'pending' && b.status !== 'pending') return -1;
      if (b.status === 'pending' && a.status !== 'pending') return 1;
      return b.date_from.localeCompare(a.date_from);
    });
  }, [requests]);

  return (
    <div className="conge-tab">
      <div className="conge-year-bar">
        <label className="conge-year-label">Année</label>
        <select
          className="conge-year-select"
          value={year}
          onChange={e => {
            const y = parseInt(e.target.value, 10);
            setYear(y);
            // Snap month to current if we jump to current year, else month=0
            if (y === currentYear) setMonth(now.getMonth());
            else setMonth(0);
          }}
        >
          <option value={currentYear - 1}>{currentYear - 1}</option>
          <option value={currentYear}>{currentYear} (en cours)</option>
          <option value={currentYear + 1}>{currentYear + 1}</option>
        </select>
      </div>

      <BalanceCards year={year} />

      {year === currentYear && <UnvalidatedWeeksPanel year={year} employmentType={employmentType} onValidateWeek={onValidateWeek} />}

      <LeaveCalendar
        year={year}
        month={month}
        onPrevMonth={goPrevMonth}
        onNextMonth={goNextMonth}
        onToday={goToday}
        entriesByDate={entriesByDate}
        onDayClick={handleDayClick}
      />

      <div className="conge-toolbar">
        <h3 className="conge-section-title">Mes demandes {year}</h3>
        {!showForm && (
          <button className="conge-btn-primary" onClick={() => { setPrefillDate(null); setShowForm(true); }}>
            <PlusIcon className="w-4 h-4" /> Nouvelle demande
          </button>
        )}
      </div>

      {showForm && (
        <NewRequestForm
          prefillDate={prefillDate}
          onSubmitted={() => { setShowForm(false); setPrefillDate(null); }}
          onClose={() => { setShowForm(false); setPrefillDate(null); }}
        />
      )}

      {isLoading ? (
        <div className="conge-loading">Chargement des demandes…</div>
      ) : sorted.length === 0 ? (
        <div className="conge-empty">
          Aucune demande pour {year}.
          {year === currentYear && !showForm && (
            <button className="conge-btn-link" onClick={() => setShowForm(true)}>Créer la première</button>
          )}
        </div>
      ) : (
        <div className="conge-list">
          {sorted.map(req => <RequestRow key={req.id} req={req} />)}
        </div>
      )}
    </div>
  );
};

export default CongesTab;
